<template>
    <div>
        <h1 class="mt-2">{{ $l10n.__('paiement') | capitalize }}</h1>
        <div class="mt-6">
            <line-2-slots :isTitle="true" class="fs-3 mb-2">
                <template slot="left">
                    {{ $l10n.__('votre abonnement') | capitalize }}
                </template>
                <template slot="right">
                    {{ formatAmount(this.centsToCurrency(this.cart.totalAmountPerMonth)) }} /
                    {{ $l10n.__('mois') }}
                </template>
            </line-2-slots>
            <template
                v-if="cartBasicProduct.customPricing && cartBasicProduct.customPricing.length"
            >
                <line-2-slots
                    v-for="(pricing, index) of cartBasicProduct.customPricing.reverse()"
                    :key="index"
                    class="fs-3 mb-2"
                >
                    <template v-if="pricing.unit_amount === 0">
                        <template slot="left">
                            {{
                                $l10n.n__(
                                    '%(nb)s utilisateur',
                                    '%(nb)s utilisateurs',
                                    pricing.nbLicences,
                                    { nb: pricing.nbLicences },
                                )
                            }}
                        </template>
                        <template slot="right">
                            {{ $l10n.n__('Offert', 'Offerts', pricing.nbLicences) }}
                        </template>
                    </template>
                    <template v-else>
                        <template slot="left">
                            {{
                                $l10n.n__(
                                    '%(nb)s utilisateur',
                                    '%(nb)s utilisateurs',
                                    pricing.nbLicences,
                                    { nb: pricing.nbLicences },
                                )
                            }}
                        </template>
                        <template slot="right">
                            {{ formatAmount(getPricingByPlan(pricing.unit_amount)) }} /
                            {{ $l10n.__('mois') }}
                        </template>
                    </template>
                </line-2-slots>
            </template>
            <line-2-slots v-else class="fs-3 mb-2">
                <template v-if="licenses.offered > 0" slot="left">
                    {{
                        $l10n.n__('%(nb)s utilisateur', '%(nb)s utilisateurs', licenses.nb, {
                            nb: licenses.nb,
                        })
                    }}
                    {{
                        $l10n.n__(
                            'dont %(offered)s offert',
                            'dont %(offered)s offerts',
                            licenses.offered,
                            { offered: licenses.offered },
                        )
                    }}
                </template>
                <template v-else slot="left">
                    {{
                        $l10n.n__('%(nb)s utilisateur', '%(nb)s utilisateurs', licenses.nb, {
                            nb: licenses.nb,
                        })
                    }}
                </template>
                <template slot="right">
                    {{ formatAmount(cartBasicProductTotalAmountPerMonth) }} / {{ $l10n.__('mois') }}
                </template>
            </line-2-slots>
            <line-2-slots class="fs-3 mb-2" v-for="product in cart.items" :key="product.id">
                <template slot="left">
                    {{ productName(product) }}
                </template>
                <template slot="right">
                    <span v-if="product == cartBasicProduct">{{
                        $l10n.__('inclus') | capitalize
                    }}</span>
                    <span v-else
                        >{{ formatAmount(centsToCurrency(product.totalAmountPerMonth)) }} /
                        {{ $l10n.__('mois') }}</span
                    >
                </template>
            </line-2-slots>
        </div>
        <!-- Don't forget to check hasVat -->
        <div class="mt-6 py-4 bt-1 bb-1 fs-3">
            <!-- New -->
            <line-2-slots v-if="isCartContext('new')" :isSubtitle="true">
                <template slot="left">
                    {{ $l10n.__("date d'activation") | capitalize }}
                </template>
                <template slot="right">
                    <span v-if="cartPeriodStartDateObj" class="red">{{
                        cartPeriodStartDateObj.unix() | dateFormatter('NORMAL_DATE', false)
                    }}</span>
                    <span v-else class="red">{{
                        now.unix() | dateFormatter('NORMAL_DATE', false)
                    }}</span>
                </template>
            </line-2-slots>
            <!-- Renew -->
            <line-2-slots
                v-else-if="
                    (isCartContext('legacy_renew') || isCartContext('renew')) &&
                    cartPeriodStartDateObj
                "
                :isSubtitle="true"
            >
                <template slot="left">
                    {{ $l10n.__('Date de renouvellement de votre abonnement') }}
                </template>
                <template slot="right">
                    <span v-if="!cartEarlyRenewal" class="red">{{
                        cartPeriodStartDateObj.unix() | dateFormatter('NORMAL_DATE', false)
                    }}</span>
                    <span v-else class="red">{{
                        now.unix() | dateFormatter('NORMAL_DATE', false)
                    }}</span>
                </template>
            </line-2-slots>
            <!-- Upsell -->
            <line-2-slots
                v-else-if="!isCartContext('new') && customerServiceEndDate"
                :isSubtitle="true"
            >
                <template slot="left">
                    {{ $l10n.__('Date de fin de votre abonnement') }}
                </template>
                <template slot="right">
                    <span>{{
                        customerServiceEndDateObj.unix() | dateFormatter('NORMAL_DATE', false)
                    }}</span>
                </template>
            </line-2-slots>

            <line-2-slots v-if="isCartContext('renew')">
                <template slot="left" v-if="cart.paymentMethod == 'stripe_card'">
                    {{
                        $l10n.__('la facture sera éditée et prélevée le %(date)s', {
                            date: $options.filters.dateFormatter(
                                cartInvoiceEditionDate.unix(),
                                'SLASHED_DATE',
                                false,
                            ),
                        }) | capitalize
                    }}
                </template>
                <template slot="left" v-else>
                    {{
                        $l10n.__('la facture sera éditée le %(date)s', {
                            date: $options.filters.dateFormatter(
                                cartInvoiceEditionDate.unix(),
                                'SLASHED_DATE',
                                false,
                            ),
                        }) | capitalize
                    }}
                </template>
            </line-2-slots>
        </div>

        <div class="mt-6" v-if="isMonthly">
            <line-2-slots :isTitle="true" class="fs-3 mb-2">
                <template slot="left">
                    {{ $l10n.__('total mensuel') | capitalize }}
                </template>
                <template slot="right">
                    {{ formatAmount(cartTotalAmountPerMonth, true) }}
                </template>
            </line-2-slots>

            <line-2-slots class="fs-3 mb-2" v-if="showTaxTotal">
                <template slot="left">
                    {{ $l10n.__('TVA') | capitalize }}
                </template>
                <template slot="right">
                    {{ formatAmount(cartTotalAmountVatPerMonth, true) }}
                </template>
            </line-2-slots>

            <line-2-slots :isSubtitle="true" class="fs-3 mb-2" v-if="showTaxTotal">
                <template slot="left">
                    {{ $l10n.__('Total mensuel TTC') | capitalize }}
                </template>
                <template slot="right">
                    {{ formatAmount(cartTotalAmountVatIncludedPerMonth, true) }}
                </template>
            </line-2-slots>

            <line-2-slots
                :isSubtitle="true"
                class="bt-1 pt-3 fs-3 mt-6 mb-2"
                v-if="cartEarlyRenewal"
            >
                <template slot="left">
                    {{ $l10n.__('échéancier') | capitalize }}
                </template>
            </line-2-slots>

            <line-2-slots class="fs-3 mb-2" v-if="cartEarlyRenewal">
                <template slot="left">
                    {{ $l10n.__('Restant à consommer') | capitalize }}
                </template>
                <template slot="right">
                    {{ formatAmount(-1 * this.centsToCurrency(cartEarlyRenewalDiscount), true) }}
                </template>
            </line-2-slots>

            <line-2-slots v-for="deadline in deadlines" :key="deadline.key" class="fs-3 mb-2">
                <template slot="left">
                    {{ deadline.date.unix() | dateFormatter('NORMAL_DATE', false) }}
                </template>
                <template slot="right">
                    {{ formatAmount(deadline.amount, true) }}
                </template>
            </line-2-slots>

            <line-2-slots v-if="deadlineUnchanged > 0 && deadlineUnchanged < 12" class="fs-3 mb-2">
                <template slot="left">
                    {{
                        $l10n.__('%(deadlineUnchanged)s échéances suivantes', {
                            deadlineUnchanged: deadlineUnchanged,
                        })
                    }}
                </template>
                <template slot="right">
                    {{ formatAmount(cartTotalAmountVatIncludedPerMonth, true) }}
                </template>
            </line-2-slots>
        </div>
        <div class="mt-6" v-else>
            <line-2-slots class="fs-3 mb-2" v-if="cartEarlyRenewal">
                <template slot="left">
                    {{ $l10n.__('Montant HT') | capitalize }}
                </template>
                <template slot="right">
                    {{ formatAmount(cartTotalAmountPerYear, true) }}
                </template>
            </line-2-slots>

            <line-2-slots class="fs-3 mb-2" v-if="cartEarlyRenewal">
                <template slot="left">
                    {{ $l10n.__('Restant à consommer') | capitalize }}
                </template>
                <template slot="right">
                    {{ formatAmount(-1 * this.centsToCurrency(cartEarlyRenewalDiscount), true) }}
                </template>
            </line-2-slots>

            <line-2-slots :isTitle="true" class="fs-3 mb-2">
                <template slot="left">
                    {{ $l10n.__('total') | capitalize }}
                </template>
                <template slot="right">
                    {{ formatAmount(totalPreviewCart.perYearDiscounted, true) }}
                </template>
            </line-2-slots>

            <line-2-slots class="fs-3 mb-2" v-if="showTaxTotal">
                <template slot="left">
                    {{ $l10n.__('TVA') }} ({{ countryTaxRate * 100 }}%)
                </template>
                <template slot="right">
                    {{ formatAmount(cartTotalAmountVatPerYear, true) }}
                </template>
            </line-2-slots>
            <line-2-slots :isSubtitle="true" class="fs-3 mb-2" v-if="showTaxTotal">
                <template slot="left">
                    {{ $l10n.__('total TTC') | capitalize }}
                </template>
                <template slot="right">
                    {{ formatAmount(cartTotalAmountVatIncludedPerYear, true) }}
                </template>
            </line-2-slots>
            <line-2-slots v-if="isPaymentFacilityEnabled" :isSubtitle="true" class="fs-3 mb-2">
                <template slot="left">
                    {{ $l10n.__('Paiement en %s fois activé', cart.paymentFacility) }}
                </template>
                <template v-if="cartNextAmountFacility < cartFirstAmountFacility" slot="right">
                    1 x {{ formatAmount(cartFirstAmountFacility, true) }}
                </template>
                <template v-else slot="right">
                    {{ cart.paymentFacility }} x {{ formatAmount(cartFirstAmountFacility, true) }}
                </template>
            </line-2-slots>
            <line-2-slots
                v-if="isPaymentFacilityEnabled && cartNextAmountFacility < cartFirstAmountFacility"
                :isSubtitle="true"
                class="fs-3 mb-2"
            >
                <template slot="right">
                    {{ cart.paymentFacility - 1 }} x
                    {{ formatAmount(cartNextAmountFacility, true) }}
                </template>
            </line-2-slots>
            <line-2-slots
                v-if="isCustomPaymentFacilityEnabled"
                :isSubtitle="true"
                class="fs-3 mb-2"
            >
                <template slot="left">
                    {{ $l10n.__('Paiement en %s fois activé', cartNbCustomFacilities) }}
                </template>
                <template v-if="cartNextAmountFacility < cartFirstAmountFacility" slot="right">
                    {{ cartNbFirstCustomFacilities }} x
                    {{ formatAmount(cartFirstAmountCustomFacility, true) }}
                </template>
                <template v-else slot="right">
                    {{ cartNbCustomFacilities }} x {{ formatAmount(cartFirstAmountFacility, true) }}
                </template>
            </line-2-slots>
            <line-2-slots
                v-if="
                    isCustomPaymentFacilityEnabled &&
                    cartFirstAmountCustomFacility < cartLastAmountCustomFacility
                "
                :isSubtitle="true"
                class="fs-3 mb-2"
            >
                <template slot="right">
                    1 x {{ formatAmount(cartLastAmountCustomFacility, true) }}
                </template>
            </line-2-slots>
        </div>

        <div class="mt-6" v-if="isCartContext('upsale')">
            <line-2-slots :isTitle="true" class="fs-3 mb-2">
                <template slot="left">
                    {{ $l10n.__('total HT supplémentaire estimé à payer*') | capitalize }}
                </template>
                <template slot="right">
                    {{
                        formatAmount(
                            newItemsOnSubscriptionTotalForCurrentPricingPlan.totalDiscounted,
                        )
                    }}
                </template>
            </line-2-slots>
            <line-2-slots class="fs-3 mb-2" v-if="showTaxTotal && countryTaxRate > 0">
                <template slot="left">
                    {{ $l10n.__('TVA estimée') }} ({{ countryTaxRate * 100 }}%)
                </template>
                <template slot="right">
                    {{ formatAmount(newItemsOnSubscriptionTotalForCurrentPricingPlan.tax) }}
                </template>
            </line-2-slots>
            <line-2-slots
                :isSubtitle="true"
                class="fs-3 mb-2"
                v-if="showTaxTotal && countryTaxRate > 0"
            >
                <template slot="left">
                    {{ $l10n.__('total TTC supplémentaire estimé à payer*') | capitalize }}
                </template>
                <template slot="right">
                    {{
                        formatAmount(
                            newItemsOnSubscriptionTotalForCurrentPricingPlan.totalTaxIncluded,
                        )
                    }}
                </template>
            </line-2-slots>
        </div>

        <!-- PRORATA TEMPORIS -->
        <div v-if="isCartContext('upsale')" class="my-5">
            <p v-if="isCurrentPricingMonthly">
                <span class="semibold">{{ $l10n.__('Attention') }} :</span>
                {{
                    $l10n.__(
                        'La prochaine facture est calculée prorata temporis, les échéances suivantes au taux plein',
                    )
                }}.
            </p>
            <p v-else class="my-5">
                *
                {{
                    $l10n.__(
                        'Ce montant est une estimation pour un prorata à cet instant. Le montant exact de la facture sera calculé au prorata du temps restant sur votre abonnement au moment du paiement.',
                    )
                }}
            </p>
        </div>

        <div class="mt-6" v-if="!hasManualPaymentMethod">
            <CompassButton
                :label="
                    (isCartContext('renew')
                        ? $l10n.__('valider mon renouvellement')
                        : $l10n.__('valider et payer mon abonnement')) | capitalize
                "
                type="primary"
                :disabled="!clickEnabled"
                @click="$emit('clicked')"
                class="wp-10"
            />
        </div>
        <div class="mt-8 b-1 p-4 br">
            <p class="fs-3 m-0">
                {{ $l10n.__('Vous pouvez partager cette page avec le lien suivant') }}
            </p>
            <p class="mt-2 mb-0 copy-element block">
                <a class="link">{{ currentUrl }}</a>
                <SaasClipboard v-if="currentUrl" :data="currentUrl" />
            </p>
        </div>
    </div>
</template>

<script>
import SaasClipboard from 'vcomponents/utilities/SaasClipboard.vue';
import WidgetMixins from '../../mixins/sellsyaccountmanager/widgetMixins';

import Line2Slots from '../utilities/Line2slots.vue';
export default {
    mixins: [WidgetMixins],
    props: {
        isMonthly: {
            type: Boolean,
            required: true,
        },
        cart: {
            type: Object,
            required: true,
        },
        clickEnabled: {
            type: Boolean,
            required: false,
            default: true,
        },
        hasManualPaymentMethod: {
            type: Boolean,
            default: false,
        },
    },
    created() {
        this.setCart(this.cart);
        this.customerSubscribedTotal = this.buildTotal(
            this.subscribedProductsIterable,
            this.billingSettings.pricingPlanCode || this.currentPricingPlan,
        );
    },
    computed: {
        now() {
            return moment();
        },
        currentUrl() {
            return window.location.href;
        },
        cartBasicProduct() {
            return this.cart.items.find(
                (c) =>
                    c.product === this.pricingBasicProduct.id ||
                    c.product_id === this.pricingBasicProduct.id,
            );
        },
        licenses() {
            return {
                nb: this.cartBasicProduct.quantity,
                offered: this.cartBasicProduct.customQuantityFree,
            };
        },
        cartPeriodStartDateObj() {
            return this.cart.periodApplianceStart ? moment(this.cart.periodApplianceStart) : null;
        },

        cartTotalAmountPerMonth() {
            return this.totalPreviewCart.perMonth;
        },
        cartTotalAmountVatPerMonth() {
            return this.totalPreviewCart.vatPerMonth;
        },
        cartTotalAmountVatIncludedPerMonth() {
            return this.totalPreviewCart.vatIncludedPerMonth;
        },
        cartTotalAmountPerYear() {
            return this.totalPreviewCart.perYear;
        },
        cartTotalAmountVatPerYear() {
            return this.totalPreviewCart.vatPerYear;
        },
        cartTotalAmountVatIncludedPerYear() {
            return this.totalPreviewCart.vatIncludedPerYear;
        },

        isPaymentFacilityEnabled() {
            return (
                !this.isCurrentPricingMonthly &&
                !this.isCartContext('upsale') &&
                this.cart.paymentFacility > 1 &&
                this.cart.paymentFacility < 12
            );
        },
        cartFacilityAmounts() {
            return this.buildFacilityAmounts(
                this.totalPreviewCart.vatIncludedPerYear,
                this.cart.paymentFacility,
            );
        },
        cartFirstAmountFacility() {
            return this.cartFacilityAmounts.firstAmount;
        },
        cartNextAmountFacility() {
            return this.cartFacilityAmounts.nextAmount;
        },

        isCustomPaymentFacilityEnabled() {
            return (
                !this.isCurrentPricingMonthly &&
                !this.isCartContext('upsale') &&
                this.cart.paymentFacility > 10000
            );
        },
        cartCustomFacilitiesAmount() {
            return this.buildCustomFacilityAmounts(
                this.totalPreviewCart.vatIncludedPerYear,
                this.cart.paymentFacility,
            );
        },
        cartNbFirstCustomFacilities() {
            return this.cartCustomFacilitiesAmount.nbFirstFacilities;
        },
        cartNbCustomFacilities() {
            return this.cartCustomFacilitiesAmount.nbFirstFacilities + 1;
        },
        cartFirstAmountCustomFacility() {
            return this.cartCustomFacilitiesAmount.firstAmount;
        },
        cartLastAmountCustomFacility() {
            return this.cartCustomFacilitiesAmount.lastAmount;
        },

        totalPreviewCart() {
            let perMonth = this.centsToCurrency(this.cart.totalAmountPerMonth);
            let perYear = this.centsToCurrency(this.cart.totalAmountPerYear);
            let perYearDiscounted = this.cartEarlyRenewal
                ? perYear - this.centsToCurrency(this.cartEarlyRenewalDiscount)
                : perYear;
            let perYearTax = perYearDiscounted * this.countryTaxRate;
            return this.buildTotalPreview(
                perMonth,
                perMonth * this.countryTaxRate,
                perYear,
                perYearTax,
                null,
                perYearDiscounted,
            );
        },

        deadlines() {
            if (!this.cartEarlyRenewal) {
                return [];
            }

            let date = moment();
            let discount = this.centsToCurrency(this.cartEarlyRenewalDiscount);
            let deadlines = [];
            for (let month = 0; month < 12; month++) {
                let amount = this.cartTotalAmountPerMonth;
                date = moment(date).add(1, 'M');

                if (discount > amount) {
                    discount -= amount;
                    amount = 0;
                } else {
                    amount -= discount;
                    discount = 0;
                }

                if (amount !== this.cartTotalAmountPerMonth) {
                    deadlines.push({
                        key: month,
                        date: date.clone(),
                        amount: this.round(amount + amount * this.countryTaxRate, 2),
                    });
                }
            }
            return deadlines;
        },
        deadlineUnchanged() {
            if (!this.cartEarlyRenewal) {
                return 0;
            }

            let discount = this.centsToCurrency(this.cartEarlyRenewalDiscount);
            let deadlinesUnchanges = 0;
            for (let month = 0; month < 12; month++) {
                let amount = this.cartTotalAmountPerMonth;

                if (discount > amount) {
                    discount -= amount;
                    amount = 0;
                } else {
                    amount -= discount;
                    discount = 0;
                }

                if (amount === this.cartTotalAmountPerMonth) {
                    deadlinesUnchanges++;
                }
            }

            return deadlinesUnchanges;
        },

        cartBasicProductTotalAmountPerMonth() {
            return this.centsToCurrency(this.cartBasicProduct.totalAmountPerMonth);
        },
        cartInvoiceEditionDate() {
            if (this.isCartContext('renew')) {
                return this.cartEarlyRenewal ? this.now : this.cartPeriodStartDateObj;
            }

            return this.cartPeriodStartDateObj || this.now;
        },
    },
    methods: {
        productName(item) {
            return this.pricingProducts.find(
                (c) => c.id === item.product || c.id === item.product_id,
            ).printableName;
        },
        centsToCurrency(cents) {
            return cents / 100;
        },
        buildTotalPreview(perMonth, vatPerMonth, perYear, vatPerYear, facility, perYearDiscounted) {
            return {
                perMonth: perMonth,
                vatPerMonth: vatPerMonth,
                vatIncludedPerMonth: perMonth + vatPerMonth,
                perYear: perYear,
                perYearDiscounted: perYearDiscounted,
                vatPerYear: vatPerYear,
                vatIncludedPerYear: perYearDiscounted + vatPerYear,
            };
        },
    },
    components: {
        SaasClipboard,
        Line2Slots,
    },
};
</script>

<style scoped lang="scss">
@import '~@sellsy/styleguide/src/_variables';
.sub-preview-title {
    font-size: 17px;
    font-weight: 700;
    color: $gray-100 !important;
}
.sub-preview-subtitle {
    font-weight: 600;
    color: $gray-100 !important;
}
</style>
